import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { filter, pairwise, takeUntil } from 'rxjs/operators';

import { formatUserName } from 'app/core/utilities/user-util';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import { Conversation, SocialEventType } from 'messages/websocket.messages';

import { ModalConvoComponent, ModalConvoData } from '../modal-convo/modal-convo.component';
import {
  ModalMusicPlayListComponent,
  ModalMusicPlayListData,
} from '../modal-music-playlist/modal-music-playlist.component';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { SideContainerType } from '../social-menu/enums/SideContainerType';
import {
  MANAGE_PRESENTERS_SUB_MENU,
  AUDIO_SETTINGS_SUB_MENU,
  PRESENTATION_SETTINGS_SUB_MENU,
  SubMenu,
} from '../social-menu/social-menu.types';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';
import { getInviteText } from '../strings';

import { ClipboardResponseService } from './clipboard-response.service';
import { ConvoService } from './convo.service';
import { HostService } from './host-service';
import { PerformerSelectorService } from './performer-selector.service';
import { SideSectionService } from './side-section.service';
import { ToastService } from './toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export class ActionMenuService extends Loggable implements OnDestroy {
  public isMenuOpen = false;
  public dialogRef?: MatDialogRef<ModalSimpleSocialComponent | ModalConvoComponent>;
  public canStartSocialEvent$ = new BehaviorSubject<boolean>(true);
  private _canStartSocialEvent = true;
  public set canStartSocialEvent(value: boolean) {
    this._canStartSocialEvent = value;
    this.canStartSocialEvent$.next(value);
  }
  public get canStartSocialEvent(): boolean {
    return this._canStartSocialEvent;
  }
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isPresenting = false;
  private isHost = false;
  protected subMenus: SubMenu[] = [];

  public constructor(
    private dialog: MatDialog,
    private sideSectionService: SideSectionService,
    private toastService: ToastService,
    private convoService: ConvoService,
    private clipboardService: ClipboardResponseService,
    private toasterService: ToastrService,
    private hostService: HostService,
    private store: Store,
    private performerSelectorService: PerformerSelectorService,
  ) {
    super();
    this.setDisplayName('ActionMenuService');
    this.sideSectionService.subMenu$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (subMenus: SubMenu[]) => {
        this.subMenus = subMenus;
      },
    });
    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    // Menu logic for presentation auto-open / close
    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(filter(notNil), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([wasPresenting, isPresenting]) => {
        this.isPresenting = isPresenting;
        if (isPresenting && !wasPresenting) {
          if (!this.isMenuOpen) {
            this.toggleMenu();
          }
          // Push Presenter settings into Sub Menu array,
          // when Manage Presenters is closed menu will show correct sub menu
          if (this.subMenus.includes(MANAGE_PRESENTERS_SUB_MENU)) {
            return;
          }
          this.sideSectionService.addSubMenu(PRESENTATION_SETTINGS_SUB_MENU);
          this.sideSectionService.openSubMenu(AUDIO_SETTINGS_SUB_MENU);
        }
        // When a non-host presenter leaves, and the audio settings submenu is open, close it. The should no longer
        // be able to change the audio settings.
        if (
          !isPresenting &&
          wasPresenting &&
          this.sideSectionService.getOpenSubMenu()?.type === AUDIO_SETTINGS_SUB_MENU.type &&
          !this.isHost
        ) {
          this.sideSectionService.openMainMenu();
        }
      });

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([oldPresentation, newPresentation]) => {
        if (this.isPresenting || !newPresentation) {
          return;
        } // don't filter nonNil to ensure we get the null to non-null change
        const { presenters: oldPresenters = {} } = oldPresentation ?? {};
        const { presenters: newPresenters = {} } = newPresentation;
        if (this.isHost && Object.keys(oldPresenters).length < 1 && Object.keys(newPresenters).length > 0) {
          if (!this.isMenuOpen) {
            this.toggleMenu();
          }
          this.sideSectionService.setOpenState(SideContainerType.ACTION_MENU);
          this.sideSectionService.addSubMenu(PRESENTATION_SETTINGS_SUB_MENU);
          this.sideSectionService.openSubMenu(MANAGE_PRESENTERS_SUB_MENU);
        }
      });
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);
    // Set initial menu state
    setTimeout(() => {
      this.setMenuState(this.sideSectionService.sideContainerType === SideContainerType.ACTION_MENU);
    });
    // Subscribe to menu state changes
    this.sideSectionService.openStateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((state) => {
      this.setMenuState(state);
    });
    this.subscribeToSocialEvents();
  }

  private setMenuState(state: boolean): void {
    this.isMenuOpen = state;
  }

  public subscribeToSocialEvents(): void {
    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      if (res !== null) {
        this.canStartSocialEvent = res.eventType === SocialEventType.NONE;
        if (res.eventType !== SocialEventType.NONE && this.dialogRef) {
          this.dialogRef.close();
        }
      }
    });
  }

  public toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.sideSectionService.setOpenState(this.isMenuOpen ? SideContainerType.ACTION_MENU : SideContainerType.NONE);
    this.hostService.requestOpenGuestList$.next(false);
    // fire resize event to resize canvas
    window.dispatchEvent(new Event('resize'));
  }

  public handleToast(): void {
    if (!this.canStartSocialEvent) {
      return;
    }
    // Can Start
    const data = {
      modalTitle: 'Make a Toast',
      text: `Making a toast will lower everyone's audio so they can tune into your announcement.
        Your mic and video will be re-enabled automatically if they have been disabled.`,
      primaryButtonText: 'Continue',
      tertiaryButtonText: 'Cancel',
    };
    this.dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data,
    });
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: string) => {
        if (result === 'primary') {
          if (this.canStartSocialEvent) {
            void this.toastService.requestStartToast();
          }
        }
        this.dialogRef = undefined;
      });
  }

  public handleConvo(): void {
    // Validate
    if (!this.canStartSocialEvent) {
      return;
    }

    // Can Start
    this.dialogRef = this.dialog.open<ModalConvoComponent, ModalConvoData>(ModalConvoComponent, {
      width: '619px',
      panelClass: 'social-modal',
      data: {
        modalTitle: 'Pick an Ice breaker Topic',
        primaryButtonText: 'Start Icebreaker',
      },
    });

    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: Conversation) => {
        if (result) {
          void this.convoService.requestStartConvo(result);
        }
        this.dialogRef = undefined;
      });
  }

  public handlePresenter(): void {
    this.performerSelectorService.open();
  }

  public handleMusicPlaylist(): void {
    // music is only available to host and needs to pop a modal
    // with dropdown select of the playlists to choose from
    // modal in designs has too much stuff from teh create modal in it - remove
    // everything except for the music playlist.
    const data = {
      modalTitle: 'Music Playlist',
      primaryButtonText: 'Update',
    };

    this.dialog.open<ModalMusicPlayListComponent, ModalMusicPlayListData>(ModalMusicPlayListComponent, {
      width: '540px',
      panelClass: 'no-padding-social-modal',
      data,
    });
  }

  public handleShareLink(firstName: string, lastName: string, socialURI: string): void {
    const name = formatUserName({ firstName, lastName });
    const invite = getInviteText(name, socialURI);

    this.clipboardService._clipboardService.copyFromContent(invite);
    this.toasterService.success('Copied to clipboard', 'Success');
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
